import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, query, orderBy, where, Timestamp } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from './firebase';
import './ManageCommissions.css';

interface Commission {
  id: string;
  bookingId: string;
  providerId: string;
  subcategoryId: string;
  commissionRate: number;
  servicePrice: number;
  commissionAmount: number;
  ownerAmount: number;
  finalPrice: number;
  createdAt: Timestamp;
  bookingCompletedAt: Timestamp;
  updatedAt?: Timestamp;
}

interface Provider {
  id: string;
  name: string;
  email: string;
  phone?: string;
}

interface Subcategory {
  id: string;
  name: string;
  categoryId: string;
}

interface CommissionStats {
  totalCommissions: number;
  totalServicePrice: number;
  totalOwnerAmount: number;
  averageCommissionRate: number;
  count: number;
}

const ManageCommissions: React.FC = () => {
  const navigate = useNavigate();
  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [filteredCommissions, setFilteredCommissions] = useState<Commission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 50;
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedProvider, setSelectedProvider] = useState<string>('');
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  
  // Stats
  const [stats, setStats] = useState<CommissionStats>({
    totalCommissions: 0,
    totalServicePrice: 0,
    totalOwnerAmount: 0,
    averageCommissionRate: 0,
    count: 0
  });

  const fetchProviders = async () => {
    try {
      console.log('Bắt đầu tải danh sách nhà cung cấp...');
      
      const providersQuery = query(
        collection(db, 'users'),
        where('role', '==', 'provider')
      );
      
      const querySnapshot = await getDocs(providersQuery);
      const providersData: Provider[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        console.log('Provider doc:', doc.id, data);
        
        providersData.push({
          id: doc.id,
          name: data.name || '',
          email: data.email || '',
          phone: data.phone || data.phoneNumber || ''
        });
      });

      console.log(`Đã tải ${providersData.length} nhà cung cấp`);
      setProviders(providersData);
      
    } catch (error) {
      console.error('Lỗi khi tải danh sách nhà cung cấp:', error);
      setProviders([]);
    }
  };

  const fetchSubcategories = async () => {
    try {
      console.log('Bắt đầu tải danh sách dịch vụ...');
      
      let subcategoriesQuery;
      let querySnapshot;

      try {
        subcategoriesQuery = query(
          collection(db, 'subcategories'),
          orderBy('order', 'asc')
        );
        console.log('Thử query subcategories với orderBy order...');
        querySnapshot = await getDocs(subcategoriesQuery);
      } catch (indexError) {
        console.warn('Query subcategories với orderBy thất bại, thử query đơn giản:', indexError);
        subcategoriesQuery = collection(db, 'subcategories');
        console.log('Thử query subcategories đơn giản...');
        querySnapshot = await getDocs(subcategoriesQuery);
      }

      const subcategoriesData: Subcategory[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        console.log('Subcategory doc:', doc.id, data);
        
        subcategoriesData.push({
          id: doc.id,
          name: data.name || '',
          categoryId: data.categoryId || ''
        });
      });

      console.log(`Đã tải ${subcategoriesData.length} dịch vụ`);
      setSubcategories(subcategoriesData);
      
    } catch (error) {
      console.error('Lỗi khi tải danh sách dịch vụ:', error);
      setSubcategories([]);
    }
  };

  const fetchCommissions = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Bắt đầu tải danh sách hoa hồng...');

      let commissionsQuery;
      let querySnapshot;

      try {
        // Server-side constraints to reduce payload
        const constraints: any[] = [];
        if (selectedProvider) constraints.push(where('providerId', '==', selectedProvider));
        if (selectedSubcategory) constraints.push(where('subcategoryId', '==', selectedSubcategory));
        if (startDate) {
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0);
          constraints.push(where('createdAt', '>=', Timestamp.fromDate(start)));
        }
        if (endDate) {
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          constraints.push(where('createdAt', '<=', Timestamp.fromDate(end)));
        }

        try {
          commissionsQuery = query(
            collection(db, 'commissions'),
            ...constraints,
            orderBy('createdAt', 'desc')
          );
          console.log('Thử query commissions có orderBy và constraints...');
          querySnapshot = await getDocs(commissionsQuery);
        } catch (indexError) {
          console.warn('Query commissions với orderBy thất bại, thử query không order:', indexError);
          commissionsQuery = query(
            collection(db, 'commissions'),
            ...constraints
          );
          console.log('Thử query commissions không order...');
          querySnapshot = await getDocs(commissionsQuery);
        }
      } catch (qErr) {
        console.warn('Xây dựng query commissions có điều kiện thất bại, fallback lấy tất cả:', qErr);
        commissionsQuery = collection(db, 'commissions');
        querySnapshot = await getDocs(commissionsQuery);
      }

      const commissionsData: Commission[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        console.log('Commission doc:', doc.id, data);
        
        commissionsData.push({
          id: doc.id,
          bookingId: data.bookingId || '',
          providerId: data.providerId || '',
          subcategoryId: data.subcategoryId || '',
          commissionRate: data.commissionRate || 0,
          servicePrice: data.servicePrice || 0,
          commissionAmount: data.commissionAmount || 0,
          ownerAmount: data.ownerAmount || 0,
          finalPrice: data.finalPrice || 0,
          createdAt: data.createdAt || new Date(),
          bookingCompletedAt: data.bookingCompletedAt || new Date(),
          updatedAt: data.updatedAt
        });
      });

      // Sắp xếp thủ công theo createdAt nếu cần
      if (commissionsData.length > 0) {
        commissionsData.sort((a, b) => {
          const aTime = a.createdAt?.toDate?.() || new Date();
          const bTime = b.createdAt?.toDate?.() || new Date();
          return bTime.getTime() - aTime.getTime();
        });
      }

      console.log(`Đã tải ${commissionsData.length} bản ghi hoa hồng`);
      setCommissions(commissionsData);
      setFilteredCommissions(commissionsData);
      
    } catch (error: any) {
      console.error('Lỗi chi tiết khi tải danh sách hoa hồng:', error);
      
      let errorMessage = 'Không thể tải danh sách hoa hồng';
      
      if (error.code === 'permission-denied') {
        errorMessage = 'Không có quyền truy cập dữ liệu. Vui lòng kiểm tra quyền đăng nhập.';
      } else if (error.code === 'unavailable') {
        errorMessage = 'Dịch vụ hiện không khả dụng. Vui lòng thử lại sau.';
      } else if (error.code === 'failed-precondition') {
        errorMessage = 'Cần tạo index cho database. Vui lòng liên hệ quản trị viên.';
      } else if (error.message) {
        errorMessage = `Lỗi: ${error.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const providerNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const p of providers) map[p.id] = p.name || '';
    return map;
  }, [providers]);

  const subcategoryNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const s of subcategories) map[s.id] = s.name || '';
    return map;
  }, [subcategories]);

  const getProviderName = (providerId: string): string => {
    return providerNameMap[providerId] || providerId || 'Không xác định';
  };

  const getSubcategoryName = (subcategoryId: string): string => {
    return subcategoryNameMap[subcategoryId] || subcategoryId || 'Không xác định';
  };

  const applyFilters = (commissionsToFilter: Commission[]) => {
    let filtered = commissionsToFilter;

    // Filter theo search term (booking ID)
    if (searchTerm.trim()) {
      filtered = filtered.filter(commission => 
        commission.bookingId.toLowerCase().includes(searchTerm.trim().toLowerCase()) ||
        getProviderName(commission.providerId).toLowerCase().includes(searchTerm.trim().toLowerCase()) ||
        getSubcategoryName(commission.subcategoryId).toLowerCase().includes(searchTerm.trim().toLowerCase())
      );
    }

    // Filter theo provider
    if (selectedProvider) {
      filtered = filtered.filter(commission => commission.providerId === selectedProvider);
    }

    // Filter theo subcategory
    if (selectedSubcategory) {
      filtered = filtered.filter(commission => commission.subcategoryId === selectedSubcategory);
    }

    // Filter theo date range
    if (startDate) {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      filtered = filtered.filter(commission => {
        const commissionDate = commission.createdAt?.toDate?.() || new Date();
        return commissionDate >= start;
      });
    }

    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      filtered = filtered.filter(commission => {
        const commissionDate = commission.createdAt?.toDate?.() || new Date();
        return commissionDate <= end;
      });
    }

    return filtered;
  };

  const calculateStats = (commissionsToCalculate: Commission[]) => {
    if (commissionsToCalculate.length === 0) {
      setStats({
        totalCommissions: 0,
        totalServicePrice: 0,
        totalOwnerAmount: 0,
        averageCommissionRate: 0,
        count: 0
      });
      return;
    }

    const totalCommissions = commissionsToCalculate.reduce((sum, c) => sum + c.commissionAmount, 0);
    const totalServicePrice = commissionsToCalculate.reduce((sum, c) => sum + c.servicePrice, 0);
    const totalOwnerAmount = commissionsToCalculate.reduce((sum, c) => sum + c.ownerAmount, 0);
    const averageCommissionRate = commissionsToCalculate.reduce((sum, c) => sum + c.commissionRate, 0) / commissionsToCalculate.length;

    setStats({
      totalCommissions,
      totalServicePrice,
      totalOwnerAmount,
      averageCommissionRate,
      count: commissionsToCalculate.length
    });
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(amount);
  };

  const formatDate = (timestamp: Timestamp): string => {
    try {
      const date = timestamp?.toDate?.() || new Date();
      return new Intl.DateTimeFormat('vi-VN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (error) {
      return 'Invalid date';
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedProvider('');
    setSelectedSubcategory('');
    setStartDate('');
    setEndDate('');
  };

  // Effect để fetch data lần đầu
  useEffect(() => {
    const fetchData = async () => {
      await Promise.all([
        fetchProviders(),
        fetchSubcategories(),
        fetchCommissions()
      ]);
    };
    fetchData();
  }, []);

  // Refetch commissions when server-side filter inputs change
  useEffect(() => {
    setCurrentPage(1);
    fetchCommissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProvider, selectedSubcategory, startDate, endDate]);

  // Memoize client-side filters (search term) and pagination
  const memoFilteredCommissions = useMemo(() => applyFilters(commissions), [commissions, providers, subcategories, searchTerm, selectedProvider, selectedSubcategory, startDate, endDate]);

  const totalPages = useMemo(() => Math.ceil(memoFilteredCommissions.length / recordsPerPage) || 1, [memoFilteredCommissions.length]);
  const displayedCommissions = useMemo(() => {
    const start = (currentPage - 1) * recordsPerPage;
    return memoFilteredCommissions.slice(start, start + recordsPerPage);
  }, [memoFilteredCommissions, currentPage]);

  useEffect(() => {
    setFilteredCommissions(memoFilteredCommissions);
    if (currentPage > totalPages) setCurrentPage(1);
    calculateStats(memoFilteredCommissions);
  }, [memoFilteredCommissions, currentPage, totalPages]);

  if (loading) {
    return (
      <div className="manage-commissions">
        <div className="page-header">
          <h2>Quản lý hoa hồng</h2>
          <p className="page-subtitle">Quản lý và theo dõi hoa hồng của các nhà cung cấp</p>
        </div>
        <div className="dashboard-loading">
          <div className="loading-spinner">Đang tải dữ liệu...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="manage-commissions">
      <div className="page-header">
        <h2>Quản lý hoa hồng</h2>
        <p className="page-subtitle">Quản lý và theo dõi hoa hồng của các nhà cung cấp</p>
      </div>

      {/* Stats Summary */}
      <div className="stats-container">
        <div className="stat-card">
          <div className="stat-value">{formatCurrency(stats.totalCommissions)}</div>
          <div className="stat-label">Tổng hoa hồng</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{formatCurrency(stats.totalServicePrice)}</div>
          <div className="stat-label">Tổng giá trị dịch vụ</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{formatCurrency(stats.totalOwnerAmount)}</div>
          <div className="stat-label">Tổng thu nhập sàn</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.averageCommissionRate.toFixed(1)}%</div>
          <div className="stat-label">Tỷ lệ hoa hồng trung bình</div>
        </div>

      </div>

      {/* Filters */}
      <div className="filters">
        <div className="filter-group">
          <label htmlFor="search-term">Tìm kiếm:</label>
          <input
            id="search-term"
            type="text"
            placeholder="Booking ID, nhà cung cấp, dịch vụ..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label htmlFor="provider-filter">Nhà cung cấp:</label>
          <select
            id="provider-filter"
            value={selectedProvider}
            onChange={(e) => setSelectedProvider(e.target.value)}
          >
            <option value="">Tất cả nhà cung cấp</option>
            {providers.map(provider => (
              <option key={provider.id} value={provider.id}>
                {provider.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="subcategory-filter">Dịch vụ:</label>
          <select
            id="subcategory-filter"
            value={selectedSubcategory}
            onChange={(e) => setSelectedSubcategory(e.target.value)}
          >
            <option value="">Tất cả dịch vụ</option>
            {subcategories.map(subcategory => (
              <option key={subcategory.id} value={subcategory.id}>
                {subcategory.name}
              </option>
            ))}
          </select>
        </div>

        <div className="date-filter-row">
          <div className="filter-group">
            <label htmlFor="start-date">Từ ngày:</label>
            <input
              id="start-date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label htmlFor="end-date">Đến ngày:</label>
            <input
              id="end-date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>

        <div className="filter-actions">
          <button onClick={clearFilters} className="clear-filters-btn">
            Xóa bộ lọc
          </button>
          <button onClick={() => {
            fetchProviders();
            fetchSubcategories();
            fetchCommissions();
          }} className="refresh-button">
            Làm mới
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <div>
            <strong>{error}</strong>
            <br />
            <small>💡 Mở Developer Console (F12) để xem thông tin chi tiết về lỗi</small>
          </div>
          <button 
            onClick={() => {
              setError(null);
              fetchProviders();
              fetchSubcategories();
              fetchCommissions();
            }}
            className="retry-button"
          >
            Thử lại
          </button>
        </div>
      )}

      {/* Commissions Table */}
      <div className="table-container">
        {displayedCommissions.length === 0 ? (
          <div className="no-data-container">
            <div className="no-data">
              {memoFilteredCommissions.length === 0 ? 'Không có dữ liệu hoa hồng' : 'Không có dữ liệu cho trang này'}
            </div>
          </div>
        ) : (
          <table className="commissions-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Booking ID</th>
                <th>Nhà cung cấp</th>
                <th>Dịch vụ</th>
                <th>Giá dịch vụ</th>
                <th>Tỷ lệ sàn (%)</th>
                <th>Hoa hồng nhà cung cấp</th>
                <th>Thu nhập sàn</th>
                <th>Ngày tạo</th>
                <th>Ngày hoàn thành</th>
              </tr>
            </thead>
            <tbody>
              {displayedCommissions.map((commission, index) => (
                <tr key={commission.id}>
                  <td className="order-cell">
                    <span className="order-display">{(currentPage - 1) * recordsPerPage + index + 1}</span>
                  </td>
                  <td>
                    <span 
                      className="booking-id-text" 
                      onClick={() => navigate(`/admin/bookings/${commission.bookingId}`)}
                      style={{ cursor: 'pointer', color: '#0066cc', textDecoration: 'underline' }}
                    >
                      {commission.bookingId}
                    </span>
                  </td>
                  <td className="provider-name">
                    <strong>{getProviderName(commission.providerId)}</strong>
                  </td>
                  <td className="subcategory-name">
                    {getSubcategoryName(commission.subcategoryId)}
                  </td>
                  <td className="service-price">
                    <span className="price-amount">{formatCurrency(commission.servicePrice)}</span>
                  </td>
                  <td className="commission-rate">
                    <span className="rate-value">{commission.commissionRate.toFixed(1)}%</span>
                  </td>
                  <td className="commission-amount">
                    <span className="commission-value">{formatCurrency(commission.commissionAmount)}</span>
                  </td>
                  <td className="owner-amount">
                    <span className="owner-value">{formatCurrency(commission.ownerAmount)}</span>
                  </td>
                  <td className="created-date">
                    <span className="date-text">{formatDate(commission.createdAt)}</span>
                  </td>
                  <td className="completed-date">
                    <span className="date-text">{formatDate(commission.bookingCompletedAt)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <div className="pagination-info">
            Hiển thị {(currentPage - 1) * recordsPerPage + 1} - {Math.min(currentPage * recordsPerPage, memoFilteredCommissions.length)} của {memoFilteredCommissions.length} kết quả
          </div>
          <div className="pagination-controls">
            <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="pagination-btn">««</button>
            <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} className="pagination-btn">«</button>
            {(() => {
              const buttons = [] as React.ReactElement[];
              const start = Math.max(1, currentPage - 2);
              const end = Math.min(totalPages, currentPage + 2);
              for (let i = start; i <= end; i++) {
                buttons.push(
                  <button key={i} onClick={() => setCurrentPage(i)} disabled={i === currentPage} className={`pagination-btn ${i === currentPage ? 'active' : ''}`}>{i}</button>
                );
              }
              return buttons;
            })()}
            <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} className="pagination-btn">»</button>
            <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="pagination-btn">»»</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageCommissions; 